export type SoundKind =
  | 'shoot'
  | 'hit'
  | 'explode'
  | 'answer-correct'   // rising 2-note (C5 → E5), short positive
  | 'answer-wrong'     // falling short (E4 → C4), not harsh
  | 'ui-click'         // 200ms short tick — soft button hover/tap
  | 'cta-click'        // task #23: stronger primary 「確認」 button tap
  | 'xp-popup'         // bling bling high freq
  | 'unlock-fanfare'   // rising 3-note fanfare (C5-E5-G5)
  | 'upgrade-pick'     // confirm sound
  | 'victory'          // rising 4-note
  | 'defeat'           // falling short sigh
  | 'lesson-correct-fanfare' // task #23: 3-note rising + bell tail
  | 'lesson-wrong-sting'     // task #23: 3-note descending sting
  | 'boss-warning'           // task #23: low rumble warning
  | 'big-boom'               // boss 2026-06-16: 大絕重爆(深沉,跟 explode 飛彈聲區隔)
  | 'skill-shield'           // boss 2026-06-16: 符文結界展開(電場嗡+鈴)
  | 'skill-scan'             // boss 2026-06-16: 機械雷達掃描(上升聲納掃)
  | 'skill-flame';           // boss 2026-06-16: 自然烈焰柱(烈焰轟鳴)

interface ShapeParams {
  durationSec: number;
  baseFreq: number; // Hz; for noise-shaped tone
  noiseAmount: number; // 0..1 mix of noise vs sine
}

/** Multi-note sequence: each note has freq, start (0..1 of total), end (0..1) */
interface Note {
  freq: number;
  startFrac: number; // fraction of total buffer where note begins
  endFrac: number;   // fraction of total buffer where note ends
  noiseAmount: number;
}

interface SequenceParams {
  durationSec: number;
  notes: Note[];
}

type SoundDef = ShapeParams | SequenceParams;

function isSequence(def: SoundDef): def is SequenceParams {
  return Array.isArray((def as SequenceParams).notes);
}

const SHAPES: Record<SoundKind, SoundDef> = {
  // ── existing ──────────────────────────────────────────────────────────────
  shoot:   { durationSec: 0.08, baseFreq: 880, noiseAmount: 0.6 },
  hit:     { durationSec: 0.12, baseFreq: 220, noiseAmount: 0.4 },
  explode: { durationSec: 0.35, baseFreq:  80, noiseAmount: 0.9 },
  // 大絕重爆:更長更低的轟鳴(synth fallback;真檔走 lowFrequency_explosion 取樣)
  'big-boom': { durationSec: 0.6, baseFreq: 48, noiseAmount: 0.95 },

  // ── new: tonal single-shape ────────────────────────────────────────────
  // ui-click: 200ms tick — mid-freq soft click
  'ui-click': { durationSec: 0.20, baseFreq: 600, noiseAmount: 0.15 },

  // xp-popup: bling — short high-freq sparkle
  'xp-popup': { durationSec: 0.18, baseFreq: 2200, noiseAmount: 0.05 },

  // upgrade-pick: confirm — warm mid tone
  'upgrade-pick': { durationSec: 0.22, baseFreq: 440, noiseAmount: 0.10 },

  // answer-wrong: falling E4→C4, two notes
  'answer-wrong': {
    durationSec: 0.30,
    notes: [
      { freq: 330, startFrac: 0.0, endFrac: 0.5, noiseAmount: 0.05 },
      { freq: 262, startFrac: 0.5, endFrac: 1.0, noiseAmount: 0.05 },
    ],
  },

  // answer-correct: rising C5→E5, two notes, short positive
  'answer-correct': {
    durationSec: 0.28,
    notes: [
      { freq: 523, startFrac: 0.0, endFrac: 0.48, noiseAmount: 0.03 },
      { freq: 659, startFrac: 0.50, endFrac: 1.0, noiseAmount: 0.03 },
    ],
  },

  // unlock-fanfare: rising C5-E5-G5
  'unlock-fanfare': {
    durationSec: 0.45,
    notes: [
      { freq: 523, startFrac: 0.0,  endFrac: 0.33, noiseAmount: 0.02 },
      { freq: 659, startFrac: 0.33, endFrac: 0.66, noiseAmount: 0.02 },
      { freq: 784, startFrac: 0.66, endFrac: 1.0,  noiseAmount: 0.02 },
    ],
  },

  // victory: rising C5-E5-G5-C6 (4 notes)
  'victory': {
    durationSec: 0.55,
    notes: [
      { freq: 523,  startFrac: 0.0,  endFrac: 0.25, noiseAmount: 0.02 },
      { freq: 659,  startFrac: 0.25, endFrac: 0.50, noiseAmount: 0.02 },
      { freq: 784,  startFrac: 0.50, endFrac: 0.75, noiseAmount: 0.02 },
      { freq: 1047, startFrac: 0.75, endFrac: 1.0,  noiseAmount: 0.02 },
    ],
  },

  // defeat: falling G4→E4→C4 sigh
  'defeat': {
    durationSec: 0.40,
    notes: [
      { freq: 392, startFrac: 0.0,  endFrac: 0.40, noiseAmount: 0.06 },
      { freq: 330, startFrac: 0.40, endFrac: 0.70, noiseAmount: 0.06 },
      { freq: 262, startFrac: 0.70, endFrac: 1.0,  noiseAmount: 0.06 },
    ],
  },

  // ── task #23 additions ────────────────────────────────────────────────
  // cta-click: stronger primary-button tap (lower fundamental + slightly longer)
  'cta-click': { durationSec: 0.18, baseFreq: 480, noiseAmount: 0.08 },

  // lesson-correct-fanfare: 3-note rising C5-E5-G5 + bell tail (C6 sparkle)
  // Distinct from unlock-fanfare via the bell tail — kid clearly hears
  // "lesson done" vs "skill unlocked".
  'lesson-correct-fanfare': {
    durationSec: 0.55,
    notes: [
      { freq: 523,  startFrac: 0.0,  endFrac: 0.30, noiseAmount: 0.02 },
      { freq: 659,  startFrac: 0.30, endFrac: 0.55, noiseAmount: 0.02 },
      { freq: 784,  startFrac: 0.55, endFrac: 0.80, noiseAmount: 0.02 },
      // bell tail — high C6 octave above for the sparkle
      { freq: 1047, startFrac: 0.80, endFrac: 1.0,  noiseAmount: 0.01 },
    ],
  },

  // lesson-wrong-sting: 3-note descending E5-C5-A4 stronger than answer-wrong
  // (which is 2-note). Used for end-of-lesson 「題目都答錯」 sting, not per-
  // question (per-question keeps answer-wrong's softer 2-note).
  'lesson-wrong-sting': {
    durationSec: 0.45,
    notes: [
      { freq: 659, startFrac: 0.0,  endFrac: 0.30, noiseAmount: 0.04 },
      { freq: 523, startFrac: 0.30, endFrac: 0.60, noiseAmount: 0.05 },
      { freq: 440, startFrac: 0.60, endFrac: 1.0,  noiseAmount: 0.06 },
    ],
  },

  // boss-warning: low rumble — A1 (55Hz) with high noise mix for warning
  // texture. Short enough not to step on the BGM, loud enough to grab
  // attention before boss appears.
  'boss-warning': { durationSec: 0.50, baseFreq: 55, noiseAmount: 0.55 },

  // ── boss 2026-06-16: 三招專屬音(synth fallback;真檔走 custom/skill-*.ogg)──
  // 符文結界:中頻電場嗡(比 boom 高、帶嗡音質地,聽起來像「罩起來」)
  'skill-shield': { durationSec: 0.45, baseFreq: 180, noiseAmount: 0.35 },
  // 機械雷達:上升雙音聲納掃(低→高,數位掃描感)
  'skill-scan': {
    durationSec: 0.40,
    notes: [
      { freq: 330, startFrac: 0.0,  endFrac: 0.5, noiseAmount: 0.10 },
      { freq: 880, startFrac: 0.5,  endFrac: 1.0, noiseAmount: 0.10 },
    ],
  },
  // 自然烈焰:低頻烈焰轟鳴(比 explode 更長更低)
  'skill-flame': { durationSec: 0.55, baseFreq: 70, noiseAmount: 0.88 },
};

/**
 * Generates a PCM mono buffer (Float32 in [-1, 1]) for the given kind.
 * Pure function — testable, no Web Audio dependency.
 */
export function synthesizeNoise(kind: SoundKind, sampleRate: number): Float32Array {
  const def = SHAPES[kind];
  const numSamples = Math.floor(def.durationSec * sampleRate);
  const out = new Float32Array(numSamples);
  const twoPi = Math.PI * 2;

  if (isSequence(def)) {
    // Multi-note sequence: each note renders over its sub-range of the buffer
    for (const note of def.notes) {
      const startIdx = Math.floor(note.startFrac * numSamples);
      const endIdx   = Math.floor(note.endFrac   * numSamples);
      const noteLen  = endIdx - startIdx;
      for (let i = startIdx; i < endIdx; i++) {
        const t        = i / sampleRate;
        const local    = (i - startIdx) / noteLen; // 0..1 within note
        const envelope = Math.sin(local * Math.PI);  // bell: rises + falls
        const sine  = Math.sin(twoPi * note.freq * t);
        const noise = Math.random() * 2 - 1;
        const sample = (sine * (1 - note.noiseAmount) + noise * note.noiseAmount) * envelope;
        out[i] = Math.max(-1, Math.min(1, sample));
      }
    }
  } else {
    // Single-shape tone with linear decay
    const { baseFreq, noiseAmount } = def;
    for (let i = 0; i < numSamples; i++) {
      const t        = i / sampleRate;
      const progress = i / numSamples;
      const envelope = 1 - progress; // linear decay
      const sine  = Math.sin(twoPi * baseFreq * t);
      const noise = Math.random() * 2 - 1;
      const sample = (sine * (1 - noiseAmount) + noise * noiseAmount) * envelope;
      out[i] = Math.max(-1, Math.min(1, sample));
    }
  }
  return out;
}

/**
 * CC0 sample overlay (Asset Pipeline B — 2026-06-05).
 *
 * Maps a SoundKind → a real CC0 OGG under public/assets/sfx/kenney/. When a
 * sample is loaded (via SoundPlayer.loadSamples) it REPLACES the harsh
 * programmatic synth for that kind; kinds without an entry keep the synth.
 *
 * Picks (all CC0 / Kenney, no attribution required — see CREDITS.md §CC0):
 *   hit          → impact-sounds metal light   (crisp metallic tank-hit)
 *   explode      → sci-fi explosionCrunch       (chunky enemy/boss boom)
 *   shoot        → sci-fi laserSmall            (replaces the 880Hz noise blip)
 *   xp-popup     → digital-audio powerUp        (skill-ready bling)
 *   upgrade-pick → interface confirmation       (升級三選一 confirm)
 *   victory      → root victory-sting.ogg       (already shipped CC0 fanfare)
 *   ui-click     → interface click              (button / UI tap)
 *   boss-warning → sci-fi lowFrequency_explosion (low rumble warning)
 *
 * Paths are relative to /public; resolved against the Vite BASE_URL at fetch
 * time. Missing files (stripped deployment / fresh checkout pre-fetch) fail
 * silently — the synth fallback covers them, so audio never 404-crashes.
 */
export const SAMPLE_PATHS: Partial<Record<SoundKind, string>> = {
  hit:            'assets/sfx/kenney/impact-sounds/impactMetal_light_001.ogg',
  explode:        'assets/sfx/kenney/sci-fi-sounds/explosionCrunch_001.ogg',
  'big-boom':     'assets/sfx/custom/meteor-boom.ogg',
  shoot:          'assets/sfx/kenney/sci-fi-sounds/laserSmall_001.ogg',
  'xp-popup':     'assets/sfx/kenney/digital-audio/powerUp4.ogg',
  'upgrade-pick': 'assets/sfx/kenney/interface-sounds/confirmation_002.ogg',
  victory:        'assets/sfx/victory-sting.ogg',
  'ui-click':     'assets/sfx/kenney/interface-sounds/click_002.ogg',
  'boss-warning': 'assets/sfx/kenney/sci-fi-sounds/lowFrequency_explosion_000.ogg',
  'skill-shield': 'assets/sfx/custom/skill-shield.ogg',
  'skill-scan':   'assets/sfx/custom/skill-scan.ogg',
  'skill-flame':  'assets/sfx/custom/skill-flame.ogg',
};

// ── W1 音效變化包(GAME_OVERHAUL_PLAN §2 W1)────────────────────────────────
//
// (1) SFX pitch 隨機:射擊/命中/爆炸類每次 play() 帶 ±10% playbackRate 隨機,
//     消除「每秒 5 發完全同音」的機械感。UI 音(按鈕/選卡/答題)不隨機 —
//     學習側回饋音要穩定可預期。
// (2) 連擊升調:KillComboPitch 在短窗口內連續擊殺時逐次 +3%、封頂 +15%、
//     窗口斷了歸零(Godot deep-dive pitch-ladder pattern,參數依 W1 spec)。

/** 會吃 ±10% 隨機音高的戰鬥音(UI 音絕不進這個集合)。 */
export const PITCH_VARIED_KINDS: ReadonlySet<SoundKind> = new Set<SoundKind>([
  'shoot',
  'hit',
  'explode',
]);

/** 隨機音高幅度:±10%。 */
export const PITCH_JITTER_RANGE = 0.10;

/**
 * Per-play random playbackRate for `kind`. Combat kinds get 1 ± 10%;
 * everything else (UI / 學習回饋音) returns exactly 1. Pure — rng injectable
 * for deterministic tests.
 */
export function randomPitchRate(kind: SoundKind, rng: () => number = Math.random): number {
  if (!PITCH_VARIED_KINDS.has(kind)) return 1;
  return 1 + (rng() * 2 - 1) * PITCH_JITTER_RANGE;
}

/** 連擊升調:每連擊 +3%。 */
export const KILL_COMBO_PITCH_STEP = 0.03;
/** 連擊升調封頂:+15%。 */
export const KILL_COMBO_PITCH_CAP = 0.15;
/** 連擊窗口:兩次擊殺間隔超過這個值 → 連擊歸零。 */
export const KILL_COMBO_WINDOW_MS = 2000;

/**
 * Kill-combo pitch ladder (pure logic, no audio dependency — testable).
 *
 * onKill(nowMs) registers a kill and returns the playbackRate the kill SFX
 * should use: 1.0 for the first kill, then +3% per consecutive kill inside
 * the 2s window, capped at +15%. A gap longer than the window resets to 1.0.
 */
export class KillComboPitch {
  private count = 0;
  private lastKillMs = Number.NEGATIVE_INFINITY;

  /** Register a kill at `nowMs` (monotonic ms clock). Returns the SFX rate. */
  onKill(nowMs: number): number {
    if (nowMs - this.lastKillMs > KILL_COMBO_WINDOW_MS) this.count = 0;
    this.lastKillMs = nowMs;
    const rate = 1 + Math.min(this.count * KILL_COMBO_PITCH_STEP, KILL_COMBO_PITCH_CAP);
    this.count++;
    return rate;
  }

  /** Consecutive kills registered in the current window (test/debug surface). */
  getCount(): number {
    return this.count;
  }

  /** Hard reset (e.g. player death / wave end). */
  reset(): void {
    this.count = 0;
    this.lastKillMs = Number.NEGATIVE_INFINITY;
  }
}

export class SoundPlayer {
  private ctx: AudioContext;
  private buffers: Map<SoundKind, AudioBuffer> = new Map();
  /** Decoded CC0 OGG samples; when present for a kind they override synth. */
  private samples: Map<SoundKind, AudioBuffer> = new Map();
  private gainMult = 1;
  /** Guards against concurrent / repeated loadSamples() calls. */
  private _samplesLoading: Promise<void> | null = null;

  constructor(ctx?: AudioContext) {
    this.ctx = ctx ?? new AudioContext();
  }

  /**
   * Best-effort: fetch + decode every CC0 OGG in SAMPLE_PATHS and cache the
   * decoded AudioBuffer per kind. Idempotent (a second call returns the same
   * in-flight / settled promise). Every fetch + decode is independently
   * try-wrapped: a 404 or decode error on one kind leaves that kind on the
   * synth fallback without aborting the others.
   *
   * Optional `baseUrl` prefixes each path (Vite import.meta.env.BASE_URL at the
   * call site). Defaults to '' so a server rooted at '/' resolves correctly.
   *
   * Safe in headless/test envs: if fetch or decodeAudioData are missing it
   * resolves immediately with no samples loaded (synth path stays active).
   */
  loadSamples(baseUrl: string = ''): Promise<void> {
    if (this._samplesLoading) return this._samplesLoading;
    const ctx = this.ctx as AudioContext & {
      decodeAudioData?: (buf: ArrayBuffer) => Promise<AudioBuffer>;
    };
    const fetchFn = (globalThis as { fetch?: typeof fetch }).fetch;
    if (typeof fetchFn !== 'function' || typeof ctx.decodeAudioData !== 'function') {
      this._samplesLoading = Promise.resolve();
      return this._samplesLoading;
    }
    const prefix = baseUrl.endsWith('/') || baseUrl === '' ? baseUrl : baseUrl + '/';
    const entries = Object.entries(SAMPLE_PATHS) as [SoundKind, string][];
    this._samplesLoading = Promise.all(
      entries.map(async ([kind, path]) => {
        try {
          const res = await fetchFn(prefix + path);
          if (!res.ok) return;
          const arr = await res.arrayBuffer();
          const decoded = await ctx.decodeAudioData!(arr);
          this.samples.set(kind, decoded);
        } catch {
          /* 404 / decode error — keep synth fallback for this kind */
        }
      }),
    ).then(() => { /* settle to void */ });
    return this._samplesLoading;
  }

  /** True iff a decoded CC0 sample is cached for `kind` (test/debug surface). */
  hasSample(kind: SoundKind): boolean {
    return this.samples.has(kind);
  }

  /** Set the SFX volume multiplier (0..1). Applied on every subsequent play(). */
  setVolume(v: number): void {
    this.gainMult = Math.max(0, Math.min(1, v));
  }

  /** Return the current SFX volume multiplier. */
  getVolume(): number {
    return this.gainMult;
  }

  ensure(kind: SoundKind): void {
    if (this.buffers.has(kind)) return;
    const pcm = synthesizeNoise(kind, this.ctx.sampleRate);
    const buf = this.ctx.createBuffer(1, pcm.length, this.ctx.sampleRate);
    buf.getChannelData(0).set(pcm);
    this.buffers.set(kind, buf);
  }

  /**
   * Play a one-shot SFX.
   *
   * W1 音效變化包: combat kinds (shoot/hit/explode) automatically get a
   * ±10% random playbackRate so rapid fire doesn't sound machine-stamped;
   * UI kinds always play at rate 1. An explicit `opts.rate` (e.g. the
   * kill-combo pitch ladder) multiplies ON TOP of the random jitter.
   * Volume/gain is never affected by pitch — parentSettings 靜音/音量
   * 仍走 setVolume()/gainMult,不被這裡動到。
   */
  play(kind: SoundKind, gain: number = 0.4, opts: { rate?: number } = {}): void {
    // Prefer a loaded CC0 OGG sample; fall back to the synth buffer otherwise.
    let buffer = this.samples.get(kind);
    if (!buffer) {
      this.ensure(kind);
      buffer = this.buffers.get(kind)!;
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const rate = (opts.rate ?? 1) * randomPitchRate(kind);
    if (rate !== 1) {
      // Guard: some test mocks build sources without a playbackRate param.
      try {
        const pr = (src as AudioBufferSourceNode).playbackRate;
        if (pr) pr.value = rate;
      } catch { /* mock without playbackRate — pitch is best-effort */ }
    }
    const g = this.ctx.createGain();
    g.gain.value = gain * this.gainMult;
    src.connect(g).connect(this.ctx.destination);
    src.start();
  }
}
